/**
 * Hook returning the current user's role on a project.
 *
 * Consumes useMembers and resolves the current Supabase user ID
 * to derive convenient boolean flags for authorization-aware UI.
 */

import { useEffect, useState } from "react";
import { authClient } from "../lib/auth";
import { useMembers } from "./useMembers";

const DEV_USER_ID = "686051fc-09c8-4b15-8981-aa55d6457a18";
const isLocalDev = typeof location !== "undefined" &&
  (location.hostname === "localhost" || location.hostname === "127.0.0.1");

export function useCurrentUserId(): string | null {
  const [userId, setUserId] = useState<string | null>(null);
  useEffect(() => {
    authClient.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? (isLocalDev ? DEV_USER_ID : null));
    });
    const {
      data: { subscription },
    } = authClient.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? (isLocalDev ? DEV_USER_ID : null));
    });
    return () => subscription.unsubscribe();
  }, []);
  return userId;
}

export interface CurrentRole {
  role: "owner" | "admin" | "editor" | "viewer";
  isOwner: boolean;
  isAdmin: boolean;
  canEdit: boolean;
  canManageMembers: boolean;
  loading: boolean;
}

export function useCurrentRole(projectId: string | undefined): CurrentRole {
  const userId = useCurrentUserId();
  const { data: members, isLoading } = useMembers(projectId);

  const membership = members?.find((m) => m.user_id === userId);
  const role = membership?.role ?? "viewer";

  return {
    role,
    isOwner: role === "owner",
    isAdmin: role === "admin" || role === "owner",
    canEdit: role !== "viewer",
    canManageMembers: role === "admin" || role === "owner",
    loading: isLoading,
  };
}
